import React, { useState } from 'react';
import { Shield, ShieldOff, Loader2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { post, getErrorMessage } from '../../utils/api';

interface EquipStockSheetProps {
  isOpen: boolean;
  onClose: () => void;
  ticker: string;
  companyName: string;
  logoUrl: string | null;
  isEquipped: boolean;
  onEquipChange: (equipped: { symbol: string; logoUrl: string } | null) => void;
}

export const EquipStockSheet: React.FC<EquipStockSheetProps> = ({
  isOpen,
  onClose,
  ticker,
  companyName,
  logoUrl,
  isEquipped,
  onEquipChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEquip = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await post<{ success: boolean; symbol: string; logoUrl: string }>(
        '/api/achievements/equip-stock',
        { symbol: ticker }
      );
      if (res.success && res.data) {
        onEquipChange({ symbol: res.data.symbol, logoUrl: res.data.logoUrl });
        onClose();
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleUnequip = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await post<{ success: boolean }>('/api/achievements/unequip-stock');
      if (res.success) {
        onEquipChange(null);
        onClose();
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      animationStyle="slide-up"
      titleCentered
      className="!max-w-sm mx-auto"
    >
      <div className="flex flex-col items-center gap-4 pb-2">
        {/* Logo preview */}
        <div className="w-24 h-24 rounded-xl bg-muted/50 border border-border flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${companyName} logo`}
              className="w-full h-full object-contain p-2"
            />
          ) : (
            <span className="text-2xl font-mono font-bold text-muted-foreground">
              {ticker}
            </span>
          )}
        </div>

        {/* Company info */}
        <div className="text-center">
          <p className="font-semibold text-foreground text-lg">{companyName}</p>
          <p className="text-sm text-muted-foreground font-mono">{ticker}</p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="w-full p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300 text-center">
            {error}
          </div>
        )}

        {/* Action button */}
        {isEquipped ? (
          <Button
            onClick={handleUnequip}
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ShieldOff className="h-4 w-4 mr-2" />
            )}
            Remove from Leaderboard
          </Button>
        ) : (
          <Button
            onClick={handleEquip}
            disabled={loading}
            className="w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Shield className="h-4 w-4 mr-2" />
            )}
            Equip as Leaderboard Icon
          </Button>
        )}
      </div>
    </Modal>
  );
};
